/**
 * Taxonomy rules:
 *   - Follow a standard format of (optional) category + (optional) adjective + noun + past tense verb
 *     - Good example: "Collaborator Account Created" (adj) (noun) (verb)
 *     - Bad example: "Created Account"
 *   - Use the following casing for events: "Upper Case Like This"
 *   - Use the following casing for properties: "all lowercase like this"
 *
 * For complete reference visit: https://webflow.atlassian.net/wiki/spaces/LIF/pages/32243894/Amplitude#Taxonomy-Rules
 *
 * This list should be alphabetized.
 */

export const ANALYTICS_EVENTS = {
  ACCOUNT_LOGIN_FAILED: 'Account Login Failed' as const,
  ACCOUNT_LOGIN_SUCCEEDED: 'Account Login Succeeded' as const,
  ACCOUNT_SIGNUP_FAILED: 'Account Signup Failed' as const,
  ACCOUNT_VERIFIED: 'Verified Account' as const,
  ADD_PANEL_VIEWED: 'Add Panel Viewed' as const,
  ADD_APP_CONNECTION_CLICKED: 'Add App Connection Clicked' as const,
  ADD_NEW_PAGE_MENU_CLICKED: 'Add New Page Menu Clicked' as const,
  ADD_NEW_PAGE_MENU_OPTION_CLICKED: 'Add New Page Menu Option Clicked' as const,
  PAGE_FILTER_OPENED: 'Page Filter Opened' as const,
  PAGE_SWITCHER_OPENED: 'Page Switcher Opened' as const,
  ADD_GOAL_BUTTON_CLICKED: 'Add Goal Button Clicked' as const,
  ADD_GOAL_TYPE_SELECTED: 'Add Goal Type Selected' as const,
  ADD_OFFLINE_CONVERSION_TOKEN_BUTTON_CLICKED:
    'Add Offline Conversion Token Button Clicked' as const,
  GOAL_CREATED: 'Goal Created' as const,
  GOAL_DELETED: 'Goal Deleted' as const,
  GOAL_EDITED: 'Goal Edited' as const,
  GOAL_RENAMED: 'Goal Renamed' as const,
  ADDED_CUSTOM_FONT: 'Added Custom Font' as const,
  ADDONS_CTA_CLICKED: 'Add-ons CTA Clicked' as const,
  ADDONS_MODAL_OPENED: 'Add-ons Modal Opened' as const,

  AIDA_QUICK_ADD_EMPTY_CANVAS_CLICKED:
    'AIDA Empty Canvas Quick Add Clicked' as const,
  AIDA_QUICK_ADD_CLICKED: 'AIDA Quick Add Section' as const,
  AIDA_SECTION_PICKER_OPTION_SELECTED:
    'AIDA Section Picker Option Selected' as const,
  AIDA_EDIT_SECTION_OPTION_SELECTED:
    'AIDA Edit Section Option Selected' as const,
  AIDA_PREVIEW_OPTIONS_SHOW_MORE_CLICKED:
    'AIDA Preview Options Show More Clicked' as const,
  AIDA_OPENED: 'AIDA Opened' as const,
  AIDA_CLOSED: 'AIDA Closed' as const,
  AIDA_CLICKED_WHILE_DISABLED: 'AIDA Clicked While Disabled' as const,
  AIDA_SECTION_PICKER_FREEFORM_PROMPT_SUBMITTED:
    'AIDA Section Picker Freeform Prompt Submitted' as const,
  AIDA_PROCESSING: 'AIDA Processing' as const,
  AIDA_ERROR_MESSAGE: 'AIDA Error Message' as const,
  AIDA_PREVIEW_VIEWED: 'AIDA Preview Viewed' as const,
  AIDA_FREEFORM_FEEDBACK_SUBMITTED: 'AIDA Freeform Feedback Submitted' as const,
  AIDA_PREVIEW_OPTIONS_SELECTED: 'AIDA Preview Options Selected' as const,
  AIDA_UNDO_CLICKED: 'AIDA Undo Clicked' as const,
  AIDA_DOWNVOTE_CLICKED: 'AIDA Downvote Clicked' as const,
  AIDA_FEEDBACK_SUBMITTED: 'AIDA Feedback Submitted' as const,
  AIDA_FEEDBACK_DOWNVOTE: 'AIDA Feedback Downvote' as const,
  AIDA_FEEDBACK_FREEFORM_SUBMITTED: 'AIDA Feedback Freeform Submitted' as const,
  AIDA_CLASSES_CREATED_CLICKED: 'AIDA Classes Created Clicked' as const,
  AIDA_CLASSES_USED_CLICKED: 'AIDA Classes Used Clicked' as const,
  AIDA_VARIABLES_USED_CLICKED: 'AIDA Variables Used Clicked' as const,
  AIDA_STYLES_APPLIED_UNDO_CLICKED: 'AIDA Styles Applied Undo Clicked' as const,
  AIDA_SECTION_PICKER_UNDO_CLICKED: 'AIDA Section Picker Undo Clicked' as const,
  AIDA_PREVIEW_OPTIONS_UNDO_CLICKED:
    'AIDA Preview Options Undo Clicked' as const,
  AIDA_ELEMENT_ADDED_TO_CANVAS: 'AIDA Element Added to Canvas' as const,
  AIDA_OPEN_STYLES_PANEL_CLICKED: 'AIDA Open Styles Panel Clicked' as const,
  AIDA_ITERATION_CATEGORY_PICKED: 'AIDA Iteration Category Picked' as const,
  AIDA_ITERATION_SUBMITTED: 'AIDA Iteration Submitted' as const,
  AIDA_ITERATION_UNDO_CLICKED: 'AIDA Iteration Undo Clicked' as const,
  AIDA_CHANGE_REQUEST_CATEGORY_PICKED:
    'AIDA Change Request Category Picked' as const,
  AIDA_CHANGE_REQUEST_FREEFORM_PROMPT_SUBMITTED:
    'AIDA Change Request Freeform Prompt Submitted' as const,
  AIDA_CHANGE_REQUEST_UNDO_CLICKED: 'AIDA Change Request Undo Clicked' as const,
  AIDA_GENERATED_PROPERTIES: 'AIDA Generated Properties' as const,
  AIDA_FTUX_VIEWED: 'AIDA FTUX Viewed' as const,
  AIDA_CONSENT_ACCEPTED: 'AIDA Consent Accepted' as const,
  AIDA_CONSENT_VIEWED: 'AIDA Consent Viewed' as const,
  AIDA_CLEAR_THREAD_CLICKED: 'AIDA Clear Thread Clicked' as const,
  AIDA_ITERATION_ADDED_TO_CANVAS: 'AIDA Iteration Added to Canvas' as const,
  AIDA_ENTERPRISE_WORKSPACE_BETA_ACCEPTED:
    'AIDA ENT Workspace Beta Agreement Accepted' as const,

  AIA_OPENED: 'AIA Opened' as const,
  AIA_CLOSED: 'AIA Closed' as const,
  AIA_RELOCATION_POSITION_CHANGED: 'AIA Relocation Position Changed' as const,
  AIA_RELOCATION_PANEL_DRAGGED: 'AIA Relocation Panel Dragged' as const,
  AIA_RELOCATION_PANEL_RESIZED: 'AIA Relocation Panel Resized' as const,
  AIA_MESSAGE_SENT: 'AIA Message Sent' as const,
  AIA_GENERATION_COMPLETE: 'AIA Generation Complete' as const,
  AIA_ERROR: 'AIA Error' as const,
  AIA_TOOL_EXECUTED: 'AIA Tool Executed' as const,
  AIA_CANCELLED: 'AIA Cancelled' as const,
  AIA_TIME_TO_FIRST_TOKEN: 'AIA Time To First Token' as const,

  // PYMT-5869 — paywall funnel (Shown → CTA Clicked → pack purchase).
  // Tracking-plan schemas still pending in segment-tracking-plan-management;
  // typed via WebflowLegacyEvents / DesignerLegacyEvents until typewriter regen.
  AI_CREDIT_WARNING_CTA_CLICKED: 'AI Credit Warning CTA Clicked' as const,
  AI_CREDIT_WARNING_SHOWN: 'AI Credit Warning Shown' as const,
  AI_HELP_CTA_CLICKED: 'AI Help CTA Clicked' as const,
  AI_SITE_BUILDER_SITE_INITIALIZED: 'AI Site Builder Site Initialized' as const,
  AI_SITE_GENERATOR_SELECTED: 'AI Site Generator Selected' as const,
  AISG_THEME_CLICKED: 'AISG Theme Clicked' as const,
  AI_HELP_OPENED: 'AI Help Opened' as const,
  AI_HELP_QUERY_CLEARED: 'AI Help Query Cleared' as const,
  AI_HELP_QUERY_ENTERED: 'AI Help Query Entered' as const,

  AGENT_INSTRUCTION_ACTION: 'Agent Instruction Action' as const,
  AGENT_INSTRUCTION_AIA_INVOKED: 'Agent Instruction AIA Invoked' as const,
  AGENT_INSTRUCTION_CREATED: 'Agent Instruction Created' as const,
  AGENT_INSTRUCTION_DELETED: 'Agent Instruction Deleted' as const,
  AGENT_INSTRUCTION_DUPLICATED: 'Agent Instruction Duplicated' as const,
  AGENT_INSTRUCTION_EDITED: 'Agent Instruction Edited' as const,
  AGENT_INSTRUCTION_EXPORTED: 'Agent Instruction Exported' as const,
  AGENT_INSTRUCTION_LIBRARY_PUBLISHED:
    'Agent Instruction Library Published' as const,
  AGENT_INSTRUCTION_SAVED: 'Agent Instruction Saved' as const,
  AGENT_INSTRUCTION_SELECTED: 'Agent Instruction Selected' as const,
  AGENT_INSTRUCTIONS_PANEL_VIEWED: 'Agent Instructions Panel Viewed' as const,
  AGENT_PRESENCE_DISPLAYED: 'Agent Presence Displayed' as const,

  AI_CONTENT_GENERATION_REQUEST_FAILED:
    'AI Content Generation Request Failed' as const,
  AI_CONTENT_GENERATION_REQUEST_FAILED_ATTEMPT:
    'AI Content Generation Request Failed Attempt' as const,
  AISB_FTU_PREVIEW_PAGE_VIEWED: 'AISB FTU Preview Page Viewed' as const,

  AISB_FEEDBACK_SUBMITTED: 'AISB Feedback Submitted' as const,
  AISB_ALL_PAGES_PREVIEW_VIEWED: 'AISB All Pages Preview Viewed' as const,
  AISB_ALL_PAGES_PREVIEW_CONTINUE_BUILDING_CLICKED:
    'AISB All Pages Preview Continue Building Clicked' as const,
  AISB_PAGE_ADDED: 'AISB Page Added' as const,
  AISB_PAGE_REMOVED: 'AISB Page Removed' as const,
  AISB_PAGE_GENERATED: 'AISB Page Generated' as const,
  AISB_SECTION_CHANGED: 'AISB Section Changed' as const,
  AISB_SECTION_ACTION_CLICKED: 'AISB Section Action Clicked' as const,
  AISB_SECTION_ADDED: 'AISB Section Added' as const,
  AISB_SELECT_PAGE_PANEL_VIEWED: 'AISB Select Page Panel Viewed' as const,
  AISB_SELECT_PAGE_CHECKBOX_CLICKED:
    'AISB Select Page Checkbox Clicked' as const,
  AISB_SELECT_PAGE_PANEL_UPGRADE_PLAN_CLICKED:
    'AISB Select Page Panel Upgrade Plan Clicked' as const,
  AISB_SELECT_PAGE_PANEL_CONTINUE_BUILDING_CLICKED:
    'AISB Select Page Panel Continue Building Clicked' as const,
  AISB_SINGLE_PAGE_PREVIEW_VIEWED: 'AISB Single Page Preview Viewed' as const,
  AISB_SITE_LOADED_IN_DESIGNER: 'AISB Site Loaded In Designer' as const,
  AISB_STEP_1_PROMPT_SUBMITTED: 'AISB Step 1 Prompt Submitted' as const,
  AISB_STEP_2_GENERATE_SITE_CLICKED:
    'AISB Step 2 Generate Site Clicked' as const,
  AISB_SITE_STRUCTURE_GENERATED: 'AISB Site Structure Generated' as const,
  AISB_EDIT_PROMPT_CLICKED: 'AISB Edit Prompt Clicked' as const,
  AISB_SITE_NAME_UPDATED: 'AISB Site Name Updated' as const,
  AISB_SITE_STRUCTURE_REGENERATED: 'AISB Site Structure Regenerated' as const,

  AISB_SITE_STRUCTURE_ADD_SECTION_CLICKED:
    'AISB Site Structure Add Section Clicked' as const,
  AISB_SITE_STRUCTURE_SECTION_ADDED:
    'AISB Site Structure Section Added' as const,
  AISB_SITE_STRUCTURE_DELETE_SECTION_CLICKED:
    'AISB Site Structure Delete Section Clicked' as const,
  AISB_SITE_STRUCTURE_DUPLICATE_SECTION_CLICKED:
    'AISB Site Structure Duplicate Section Clicked' as const,
  AISB_SITE_STRUCTURE_INSERT_SECTION_CLICKED:
    'AISB Site Structure Insert Section Clicked' as const,
  AISB_SITE_STRUCTURE_LOADED: 'AISB Site Structure Loaded' as const,
  AISB_SITE_STRUCTURE_SECTION_MOVED:
    'AISB Site Structure Section Moved' as const,
  AISB_AI_ENTRYPOINT_ERROR: 'AISB AI Entrypoint Error' as const,
  AISB_SITE_LIMIT_REACHED: 'AISB Site Limit Reached' as const,
  AISB_INSUFFICIENT_SITE_CREATION_PERMISSIONS:
    'AISB Insufficient Site Creation Permissions' as const,
  AISG_FTU_PREVIEW_GENERATE_MORE_CLICKED:
    'AISG FTU Preview Generate More Clicked' as const,
  AISG_FTU_PREVIEW_PACK_CONTROL_CLICKED:
    'AISG FTU Preview Pack Control Clicked' as const,
  AISG_FTU_PREVIEW_THEME_TILE_CLICKED:
    'AISG FTU Preview Theme Tile Clicked' as const,
  AISG_FTU_PREVIEW_EDIT_PROMPT_CLICKED:
    'AISG FTU Preview Edit Prompt Clicked' as const,
  AISG_FTU_PREVIEW_REGENERATE_CLICKED:
    'AISG FTU Preview Regenerate Clicked' as const,
  AISG_FTU_PREVIEW_EXIT_CLICKED: 'AISG FTU Preview Exit Clicked' as const,
  AISG_FTU_PREVIEW_START_BUILDING_CLICKED:
    'AISG FTU Preview Start Building Clicked' as const,
  AISG_FTU_PREVIEW_TIME_TO_BUILD: 'AISG FTU Preview Time to Build' as const,
  AISG_HOVERED_SECTION_CONTROLS_CLICKED:
    'AISG Hovered Section Controls Clicked' as const,
  AISG_IMAGE_GENERATION_REQUEST_FAILED:
    'AISG Image Generation Request Failed' as const,
  AISG_PACK_SELECTED: 'AISG Pack Selected' as const,
  AISG_PAGE_GENERATION_MODAL_CTA_CLICKED:
    'AISG Page Generation Modal CTA Clicked' as const,

  ALIGN_TAG_CLICKED: 'Align Tag Clicked' as const,
  ALIGN_TAG_VIEWED: 'Align Tag Viewed' as const,
  ANALYZE_PUBLISH_PROMO_CLICKED: 'Analyze Publish Promo Clicked' as const,
  ANALYZE_PUBLISH_PROMO_VIEWED: 'Analyze Publish Promo Viewed' as const,
  ANALYZE_COMPETE_GROWTH_IPN_SHOWN: 'Analyze Compete Growth IPN Shown' as const,
  ANALYZE_COMPETE_GROWTH_IPN_DISMISSED:
    'Analyze Compete Growth IPN Dismissed' as const,
  ANALYZE_COMPETE_GROWTH_IPN_CLICKED:
    'Analyze Compete Growth IPN Clicked' as const,
  ANALYZE_COMPETE_GROWTH_IPN_LEARN_MORE_CLICKED:
    'Analyze Compete Growth IPN Learn More Clicked' as const,
  ANNOTATION_COMMENT_VIEWED: 'Annotation Comment Viewed' as const,
  ANNOTATION_METADATA_VIEWED: 'Annotation Metadata Viewed' as const,
  ANNOTATION_NEW_COMMENT_INITIATED: 'Annotation New Comment Initiated' as const,
  ANNOTATION_OVERLAY_TOGGLED: 'Annotation Overlay Toggled' as const,
  ANNOTATION_PANEL_FILTER_OPENED: 'Annotation Panel Filter Opened' as const,
  ANNOTATION_PANEL_FILTER_CLOSED: 'Annotation Panel Filter Closed' as const,
  ANNOTATION_PREVIEW_HOVERED: 'Annotation Preview Hovered' as const,
  API_TOKEN_GENERATED: 'API Token Generated' as const,
  APP_CONNECTION_ADDED: 'App Connection Added' as const,
  APP_CONNECTION_REMOVED: 'App Connection Removed' as const,
  APP_INSTALL_CLICKED: 'App install clicked from Designer' as const,
  APP_LAUNCHED_ON_INSTALL: 'App Launch After Install' as const,
  APP_UPDATED: 'App Updated' as const,
  ASSETS_PANEL_ADD_FOLDER_CLICKED: 'Assets Panel Add Folder Clicked' as const,
  ASSETS_PANEL_BULK_COMPRESS_MODAL_OPENED:
    'Assets Panel Bulk Compress Modal Opened' as const,
  ASSETS_PANEL_BULK_DELETE_MODAL_OPENED:
    'Assets Panel Bulk Delete Modal Opened' as const,
  ASSETS_PANEL_BULK_ITEM_ALL_TOGGLED:
    'Assets Panel Bulk Item All Toggled' as const,
  ASSETS_PANEL_BULK_ITEM_TOGGLED: 'Assets Panel Bulk Item Toggled' as const,
  ASSETS_PANEL_BULK_MOVE_MODAL_OPENED:
    'Assets Panel Bulk Move Modal Opened' as const,
  ASSETS_PANEL_FILE_UPLOAD_CLICKED: 'Assets Panel File Upload Clicked' as const,
  ASSETS_PANEL_FILTER_CLEARED: 'Assets Panel Filter Cleared' as const,
  ASSETS_PANEL_FILTER_CLICKED: 'Assets Panel Filter Clicked' as const,
  ASSETS_PANEL_FULL_VIEW_TOGGLED: 'Assets Panel Full View Toggled' as const,
  ASSETS_PANEL_LIST_DISPLAY_SELECTED:
    'Assets Panel List Display Selected' as const,
  ASSETS_PANEL_SEARCH_INPUT_CHANGED:
    'Assets Panel Search Input Changed' as const,
  ASSETS_PANEL_SEARCH_INPUT_CLEARED:
    'Assets Panel Search Input Cleared' as const,
  ASSETS_PANEL_SORT_SELECTED: 'Assets Panel Sort Selected' as const,
  ASSETS_UPLOADED: 'Asset Manager Asset Uploaded' as const,
  BANDWIDTH_REPORT_FILTER_APPLIED: 'Bandwidth Report Filter Applied' as const,
  BANDWIDTH_REPORT_SEARCH_APPLIED: 'Bandwidth Report Search Applied' as const,
  BANDWIDTH_REPORT_SORT_CHANGED: 'Bandwidth Report Sort Changed' as const,
  BANNER_VIEWED: 'Banner Viewed' as const,
  BECOME_A_PARTNER_PAGE_VIEWED: 'Become A Partner Page Viewed' as const,
  BETA_FEATURE_TOGGLED: 'Beta Feature Toggled' as const,
  BILLING_ACTION: 'Billing Action Tracked' as const,
  BLANK_SITE_SELECTED: 'Blank Site Selected' as const,
  BREAKPOINT_ADDED: 'Breakpoint Added' as const,
  BREAKPOINT_CLICKED: 'Breakpoint Clicked' as const,
  BULK_REDIRECT_IMPORTED: 'Bulk Redirect Imported' as const,
  CANVAS_BAR_PINNED: 'Canvas Bar Pinned' as const,
  CANVAS_SETTINGS_CLICKED: 'Canvas Settings Clicked' as const,
  CANVAS_SPACING_PEEKED: 'Canvas Spacing Peeked' as const,
  CANVAS_VIEW_CHANGED: 'Canvas View Changed' as const,
  CLASS_CREATED: 'Class Created' as const,
  CLASS_UPDATED: 'Class Updated' as const,
  CMS_ACCESS_UPDATED: 'CMS Access Updated' as const,
  CMS_ITEM_PUBLISHED: 'CMS Item Published' as const,
  CMS_ITEM_PUBLISH_FAILED: 'CMS Item Publish Failed' as const,
  CMS_MULTI_REFERENCE_ITEM_REORDERED:
    'CMS Multi Reference Item Reordered' as const,
  CHECKLIST_ITEM_CLICKED: 'Checklist Item Clicked' as const,
  CHECKOUT_ADDRESS_AUTOCOMPLETE: 'Checkout Address Autocomplete' as const,
  CHECKOUT_EDIT_PAYMENT_METHOD: 'Checkout Edit Payment Method Clicked' as const,
  CHECKOUT_MODAL_CLOSED: 'Checkout Modal Closed' as const,
  CHECKOUT_MODAL_OPENED: 'Checkout Modal Opened' as const,
  CHECKOUT_PAYMENT_BUTTON_PRESSED: 'Checkout Payment Button Pressed' as const,
  CHECKOUT_POST_SUCCESS_SIDE_EFFECT_FAILED:
    'Checkout Post Success Side Effect Failed' as const,
  CLOUD_D1_ROW_ADDED: 'Cloud D1 Row Added' as const,
  CLOUD_D1_ROW_DELETED: 'Cloud D1 Row Deleted' as const,
  CLOUD_D1_ROW_UPDATED: 'Cloud D1 Row Updated' as const,
  CLOUD_KV_ENTRY_SET: 'Cloud KV Entry Set' as const,
  CLOUD_KV_ENTRY_DELETED: 'Cloud KV Entry Deleted' as const,
  CLOUD_R2_OBJECT_DELETED: 'Cloud R2 Object Deleted' as const,
  CLOUD_R2_OBJECT_UPLOADED: 'Cloud R2 Object Uploaded' as const,
  CODE_COMPONENT_CREATED: 'Code Component Created' as const,
  CODE_COMPONENT_INSTANCE_ERRORED: 'Code Component Instance Errored' as const,
  CODE_COMPONENT_SAFE_MODE_DISABLED:
    'Code Component Safe Mode Disabled' as const,
  CODE_COMPONENT_SAFE_MODE_ENABLED: 'Code Component Safe Mode Enabled' as const,
  COLLECTION_ITEM_CREATE_CANCELLATION:
    'Collection Item Create Cancellation' as const,
  COLLECTION_ITEM_CREATED: 'Collection Item Created' as const,
  COLLECTION_ITEM_SCHEDULED: 'Collection Item Scheduled' as const,
  COLLECTION_LIST_SETTINGS_CURATED_CHANGE_QUERY_MODE_CANCELLED:
    'Collection List Settings Curated Warning Modal Cancelled' as const,
  COLLECTION_LIST_SETTINGS_CURATED_CHANGE_QUERY_MODE_CONFIRMED:
    'Collection List Settings Curated Warning Modal Confirmed' as const,
  COLLECTION_LIST_SETTINGS_CURATED_CHANGE_QUERY_MODE_MODAL_OPENED:
    'Collection List Settings Curated Warning Modal Opened' as const,
  COLLECTION_LIST_SETTINGS_CURATED_FILTER_APPLIED:
    'Collection List Settings Curated Filter Applied' as const,
  COLLECTION_LIST_SETTINGS_CURATED_FILTER_CLEARED:
    'Collection List Settings Curated Filter Cleared' as const,
  COLLECTION_LIST_SETTINGS_CURATED_ITEMS_REORDERED:
    'Collection List Settings Curated Items Reordered' as const,
  COLLECTION_LIST_SETTINGS_CURATED_ITEM_SELECTOR_CLOSED:
    'Collection List Settings Curated Item Select Closed' as const,
  COLLECTION_LIST_SETTINGS_CURATED_ITEM_SELECTOR_OPENED:
    'Collection List Settings Curated Item Select Opened' as const,
  COLLECTION_LIST_SETTINGS_CURATED_REMOVE_ALL_BUTTON_CLICKED:
    'Collection List Settings Curated Remove All Items Clicked' as const,
  COLLECTION_LIST_SETTINGS_CURATED_SELECT_ALL_BUTTON_CLICKED:
    'Collection List Settings Curated Select All Items Clicked' as const,
  COLLECTION_LIST_SETTINGS_CURATED_TOGGLE_CLICKED:
    'Collection List Settings Curated Toggle Clicked' as const,
  COLLECTION_LIST_SETTINGS_DYNAMIC_CHANGE_QUERY_MODE_CANCELLED:
    'Collection List Settings Dynamic Warning Modal Cancelled' as const,
  COLLECTION_LIST_SETTINGS_DYNAMIC_CHANGE_QUERY_MODE_CONFIRMED:
    'Collection List Settings Dynamic Warning Modal Confirmed' as const,
  COLLECTION_LIST_SETTINGS_DYNAMIC_CHANGE_QUERY_MODE_MODAL_OPENED:
    'Collection List Settings Dynamic Warning Modal Opened' as const,
  COLLECTION_LIST_SETTINGS_DYNAMIC_TOGGLE_CLICKED:
    'Collection List Settings Dynamic Toggle Clicked' as const,
  COLLECTION_LIST_VIEWED: 'Collection List Viewed' as const,
  COMPONENT_INSTANCE_VIEW_ITEM_SELECTED:
    'Component Instance View Item Selected' as const,
  COMPONENT_INSTANCE_VIEW_OPENED: 'Component Instance View Opened' as const,
  CONTENT_EDITOR_ADD_MENU_OPENED: 'Content Editor Add Menu Opened' as const,
  CONTENT_EDITOR_COMPONENT_VALIDATION_FAILED:
    'Content Editor Component Validation Failed' as const,
  CONTENT_EDITOR_ELEMENT_ADDED: 'Content Editor Element Added' as const,
  CONTENT_EDITOR_ELEMENT_REMOVED: 'Content Editor Element Removed' as const,
  CONTENT_EDITOR_IMAGE_UPLOADED: 'Content Editor Image Uploaded' as const,
  CONTENT_EDITOR_TEXT_EDITED: 'Content Editor Text Edited' as const,
  AISB_SITE_PUBLISHED: 'AISB Site Published' as const,
  AISB_PUBLISH_PAYWALL_VIEWED: 'AISB Publish Paywall Viewed' as const,
  AISB_PUBLISH_PAYWALL_UPGRADE_PLAN_CLICKED:
    'AISB Publish Paywall Upgrade Plan Clicked' as const,
  CREATE_PAGE_CALLOUT_VIEWED: 'Create Page Callout Viewed' as const,
  CREATE_PAGE_CALLOUT_DISMISSED: 'Create Page Callout Dismissed' as const,
  CREATE_PROJECT_SELECTED: 'Create Project Selected' as const,
  CTA_CLICKED: 'CTA Clicked' as const,
  // Instruments the custom attribute paste auto-split shipped in PYMT-6307.
  // Tracking-plan schema still pending in segment-tracking-plan-management;
  // typed via DesignerLegacyEvents until typewriter regen.
  CUSTOM_ATTRIBUTE_SPLIT_ON_PASTE: 'Custom Attribute Split On Paste' as const,
  CUSTOM_ROLE_MODAL_OPENED: 'Custom Role Modal Opened' as const,
  CUSTOM_ROLE_CREATED: 'Custom Role Created' as const,
  CUSTOM_ROLE_DELETED: 'Custom Role Deleted' as const,
  CUSTOM_ROLE_UPDATED: 'Custom Role Updated' as const,
  CUSTOM_ROLE_EDIT_BUTTON_CLICKED: 'Custom Role Edit Button Clicked' as const,
  CUSTOM_ROLE_DUPLICATE_AND_EDIT_BUTTON_CLICKED:
    'Custom Role Duplicate And Edit Button Clicked' as const,
  BRIGHTNESS_SET: 'Brightness Set' as const,
  DASHBOARD_SORT_SELECTED: 'Dashboard Sort Selected' as const,
  DASHBOARD_THEME_TOGGLED: 'Dashboard Theme Toggled' as const,
  THEMES_ANNOUNCEMENT_MODAL_SHOWN: 'Themes Announcement Modal Shown' as const,
  THEME_SYNC_REMINDER_TOAST_CLOSED: 'Theme Sync Reminder Toast Closed' as const,
  THEME_SYNC_REMINDER_TOAST_SHOWN: 'Theme Sync Reminder Toast Shown' as const,
  DASHBOARD_VIEWED: 'Dashboard Viewed' as const,
  DASHBOARD_VIEW_TOGGLED: 'Dashboard View Toggled' as const,
  DATA_EXPORT_DESTINATION_EDITED: 'Data Export Destination Edited' as const,
  DATA_EXPORT_DESTINATION_REMOVED: 'Data Export Destination Removed' as const,
  DATA_EXPORT_DESTINATION_SAVED: 'Data Export Destination Saved' as const,
  DATA_EXPORT_DESTINATION_SELECTED: 'Data Export Destination Selected' as const,
  DATA_EXPORT_MODAL_OPENED: 'Data Export Modal Opened' as const,
  DEPLOY_CLOUD_DASHBOARD_OPENED: 'Deploy Cloud Dashboard Opened' as const,
  DEPLOY_CREATE_APP_INITIATED: 'Deploy Create App Initiated' as const,
  DEPLOY_ENTRY_BUTTON_CLICKED: 'Deploy Entry Button Clicked' as const,
  DEPLOY_FAILED: 'Deploy Failed' as const,
  DEPLOY_GITHUB_AUTHORIZATION_COMPLETED:
    'Deploy GitHub Authorization Completed' as const,
  DEPLOY_GITHUB_AUTHORIZATION_PAGE_SHOWN:
    'Deploy GitHub Authorization Page Shown' as const,
  DEPLOY_GITHUB_CONNECT_CLICKED: 'Deploy GitHub Connect Clicked' as const,
  DEPLOY_GITHUB_CONNECTION_PAGE_SHOWN:
    'Deploy GitHub Connection Page Shown' as const,
  DEPLOY_GITHUB_PERMISSIONS_SCREEN_SHOWN:
    'Deploy GitHub Permissions Screen Shown' as const,
  DEPLOY_GITHUB_REVIEW_UPDATE_CLICKED:
    'Deploy GitHub Review Update Clicked' as const,
  GITHUB_ACCOUNT_CONNECTED: 'GitHub Account Connected' as const,
  GITHUB_ACCOUNT_DISCONNECTED: 'GitHub Account Disconnected' as const,
  GITHUB_AUTH_CLICKED: 'GitHub Auth Clicked' as const,
  GITHUB_CONNECT_CLICKED: 'GitHub Connect Clicked' as const,
  GITHUB_DISCONNECT_CLICKED: 'GitHub Disconnect Clicked' as const,
  ACCOUNT_SETTINGS_VIEWED: 'Account Settings Viewed' as const,
  DEPLOY_INITIATED: 'Deploy Initiated' as const,
  DEPLOY_PAGE_VIEWED: 'Deploy Page Viewed' as const,
  DEPLOY_PLAN_LIMIT_BANNER_SHOWN: 'Deploy Plan Limit Banner Shown' as const,
  DEPLOY_RESTART_CLICKED: 'Deploy Restart Clicked' as const,
  DEPLOY_RESTART_SCREEN_SHOWN: 'Deploy Restart Screen Shown' as const,
  DEPLOY_SOURCE_SELECTED: 'Deploy Source Selected' as const,
  DEPLOY_SUCCEEDED: 'Deploy Succeeded' as const,
  DEPLOY_UPGRADE_CLICKED: 'Deploy Upgrade Clicked' as const,
  DATA_MANAGER_MODAL_OPENED: 'Data Manager Modal Opened' as const,
  DEFAULT_WORKSPACE_SET: 'Default Workspace Set' as const,
  DEFAULT_DOMAIN_SET: 'Default Domain Set' as const,
  DELAYED_SEGMENTATION_SURVEY_VIEWED:
    '[Client payments] Delayed Segmentation Survey Viewed' as const,
  DOMAIN_PUBLISHING_VIEWED: 'Domain Publishing Viewed' as const,
  DESIGNER_EXTENSION_VERSION_SUBMITTED:
    'Designer Extension Version Submitted' as const,
  DESIGNER_OPENED: 'Designer Opened' as const,
  DESIGNER_LOCALESLIST_TOOLTIP_UPGRADE_LINK_CLICKED:
    'Designer Locales List Element Tooltip Upgrade Link Clicked' as const,
  DESIGNER_SETTINGS_PANEL_OPENED: 'Designer Settings Panel Opened' as const,
  DESIGNER_FRICTIONLESS_INVITE_PURCHASED:
    'Designer frictionless invite purchased' as const,
  DESIGNER_API_METHOD_CALLED: 'Designer API Method Called' as const,
  DESIGNER_UNPUBLISH_CLICKED: 'Designer Unpublish Clicked' as const,
  DISABLED_TWO_FACTOR_AUTHENTICATION:
    'Disabled Two-Factor Authentication' as const,
  DISMISSED_BUTTON_CLICKED: 'Dismissed Button Clicked' as const,
  APP_CREATED: 'App Created' as const,
  APP_CLOSED: 'App Closed' as const,
  APP_LAUNCHED: 'App Launched' as const,
  APP_INSTALLED: 'App Installed' as const,
  APP_WINDOW_TOGGLED: 'App Window Toggled' as const,
  APP_GEN_LANDING_PAGE_SITE_WELCOME_MODAL_SHOWN:
    'App Gen Landing Page Site Welcome Modal Shown' as const,
  APP_GEN_LANDING_PAGE_SITE_WELCOME_MODAL_CLOSED:
    'App Gen Landing Page Site Welcome Modal Closed' as const,
  CALENDLY_EVENT_SCHEDULED: 'Calendly Event Scheduled' as const,
  CALENDLY_EVENT_TYPE_VIEWED: 'Calendly Event Type Viewed' as const,
  CLI_TELEMETRY: 'Webflow CLI Telemetry' as const,
  CLIENT_INVITE_ACCEPTED: '[Client payments] Client Invite Accepted' as const,
  CLIENT_INVITE_PAGE_VIEWED:
    '[Client payments] Client Invite Page Viewed' as const,
  DELETE_CMS_MODAL_CLOSED: 'Delete CMS Modal Closed' as const,
  DELETE_CMS_MODAL_OPENED: 'Delete CMS Modal Opened' as const,
  DEVLINK_COMPONENTS_SYNCED: 'Devlink Components Synced' as const,
  DEVLINK_COMPONENTS_SYNC_FAILED: 'Devlink Components Sync Failed' as const,
  DEVLINK_CONFIG_OPENED: 'Devlink Config Opened' as const,
  DOWNGRADE_PLAN_BUTTON_PRESSED: 'Downgrade Plan Button Pressed' as const,
  DROPDOWN_OPENED: 'Dropdown Opened' as const,
  DROPDOWN_SELECTED: 'Dropdown Selected' as const,
  ECOMMERCE_MEMBERSHIP_PRODUCT_LIMIT_EXCEEDED:
    'Ecommerce Membership Product Limit Exceeded' as const,
  ECOMMERCE_PRODUCT_CSV_EXPORTED: 'Ecommerce Product CSV Exported' as const,
  EDITOR_OPENED: 'Editor Opened' as const,
  ELEMENT_ADDED: 'Element Added' as const,
  ELEMENT_COPIED: 'Element Copied' as const,
  ELEMENT_CUT: 'Element Cut' as const,
  ELEMENT_DELETED: 'Element Deleted' as const,
  ELEMENT_RENAME_BLOCKED: 'Element Rename Blocked' as const,
  ELEMENT_SETTINGS_UPDATED: 'Element Settings Updated' as const,
  ELEMENT_UNWRAPPED: 'Element Unwrapped' as const,
  ELEMENT_WRAPPED: 'Element Wrapped' as const,
  EMAIL_ENTRY_PAGE_VIEWED: 'Email Entry Page Viewed' as const,
  EMAIL_SUGGESTION_USED: 'Email Suggestion Used' as const,
  EMAIL_SUGGESTION_PAGE_VIEWED: 'Email Suggestion Page Viewed' as const,
  EMAIL_VERIFICATION_COMPLETED: 'Email Verification Completed' as const,
  EMAIL_VERIFICATION_RESEND_CTA_CLICKED:
    'Email Verification Resend CTA Clicked' as const,
  EMAIL_VERIFICATION_RESEND_FAILED: 'Email Verification Resend Failed' as const,
  EMAIL_VERIFICATION_RESEND_SUCCEEDED:
    'Email Verification Resend Succeeded' as const,
  EMAIL_VERIFICATION_SCHEDULED: 'Email Verification Scheduled' as const,
  EMAIL_VERIFICATION_SENT: 'Email Verification Sent' as const,
  EMAIL_VERIFICATION_STARTED: 'Email Verification Started' as const,
  ENABLED_TWO_FACTOR_AUTHENTICATION:
    'Enabled Two-Factor Authentication' as const,
  ENTER_PASSWORD_PAGE_VIEWED: 'Enter Password Page Viewed' as const,
  ENTERPRISE_LEAD_REGISTERED: 'Enterprise Lead Registered' as const,
  ENTERPRISE_PRICING_OVERLAY_DISPLAYED: 'Enterprise Overlay Displayed' as const,
  ENTERPRISE_PRICING_OVERLAY_FORM_DISPLAYED: 'CTA Clicked' as const,
  ENTERPRISE_PRICING_OVERLAY_FORM_SUBMITTED: 'Website Form Submitted' as const,
  ENTRI_CONNECT_CLICKED: 'Clicked Entri Connect' as const,
  ENTRI_CONNECT_CANCELLED: 'Cancelled Entri Connect' as const,
  ENTRI_CONNECT_MANUAL_COMPLETED: 'Completed Manual Entri Connect' as const,
  ENTRI_CONNECT_AUTOMATIC_COMPLETED:
    'Completed Automatic Entri Connect' as const,
  ENTRI_PURCHASE_CLICKED: 'Clicked Entri Purchase' as const,
  ENTRI_PURCHASE_CANCELLED: 'Cancelled Entri Purchase' as const,
  ENTRI_PURCHASE_COMPLETED: 'Completed Entri Purchase' as const,
  EXCLUSION_RULE_ADDED: 'Exclusion Rule Added' as const,
  EXCLUSION_RULE_REMOVED: 'Exclusion Rule Removed' as const,
  EXCLUSION_RULE_BULK_UPDATED: 'Exclusion Rule Bulk Updated' as const,
  EXISTING_GOAL_ADDED: 'Existing Goal Added' as const,
  EXISTING_GOAL_REMOVED: 'Existing Goal Removed' as const,
  EXPERIMENT_ASSIGNED_PRIORITIZE_CLONEABLE_CONTENT_FOR_SEO:
    'Experiment Assigned - Prioritize cloneable content for SEO' as const,
  EXPERIMENT_ASSIGNED_SHOW_TEMPLATES_ON_MIW_TAG_PAGES:
    'Experiment Assigned - Show templates on MIW tag pages' as const,
  ERROR_ENCOUNTERED: 'Error Encountered' as const,
  ADD_EXISTING_DOMAIN_MANUAL_CLICKED:
    'Clicked Add Manual Existing Domain' as const,
  ADD_EXISTING_DOMAIN_MANUAL_COMPLETED:
    'Completed Add Manual Existing Domain' as const,
  EXPERT_PROFILE_CREATED: 'Expert profile created' as const,
  FAILED_CHARGES_NOTICE_VIEWED: 'Show Failed Charges Notice' as const,
  FAILED_CHARGES_NOTICE_CLICKED: 'Clicked Failed Charges Notice' as const,
  FEEDBACK_SENT: 'Feedback Sent' as const,
  FIGMA_PASTED: 'Figma Pasted' as const,
  FINDER_ITEM_SELECTED: 'Finder Search Item Selected' as const,
  FINDER_TRIGGERED: 'Finder Toggled' as const,
  FLEX_ONCANVAS_CONTROLS_VIEWED: 'Flex On Canvas Controls Viewed' as const,
  FORGOT_PASSWORD_PAGE_VIEWED: 'Forgot Password Page Viewed' as const,
  FORM_GOAL_CREATED_VIA_INTEGRATION:
    'Form Goal Created Via Integration' as const,
  FORM_SELECT_BULK_ADD_MODAL_OPENED:
    'Form Select Bulk Add Modal Opened' as const,
  FORM_SELECT_BULK_OPTIONS_ADDED: 'Form Select Bulk Options Added' as const,
  FORM_SUBMISSION_NOTIFICATIONS_MODIFIED:
    'Form Submission Notifications Modified' as const,
  FORM_SUBMISSION_NOTIFICATIONS_DROPDOWN_OPENED:
    'Form Submission Notifications Dropdown Opened' as const,
  FOUNDATIONS_ENROLLMENT_FORM_SUBMITTED:
    'Foundations Enrollment Form Submitted' as const,
  FOUNDATIONS_ENROLLMENT_FORM_VIEWED:
    'Foundations Enrollment Form Viewed' as const,
  GUEST_INVITE_MODAL_VIEWED: '[Invite guest modal] Modal viewed' as const,
  GUEST_INVITE_MODAL_ALLOWED_ROLES_CHANGED:
    '[Invite guest modal] Allowed roles changed' as const,
  GUEST_INVITE_MODAL_ADVANCED_PERMISSIONS_CHANGED:
    '[Invite guest modal] Advanced permission changed' as const,
  GUEST_INVITE_MODAL_EMAIL_ENTERED:
    '[Invite guest modal] Guest email entered' as const,
  GUEST_INVITE_BLOCKED: '[Invite guest modal] Invite blocked' as const,
  GUEST_INVITE_SENT: '[Invite guest modal] Invite sent' as const,
  GUEST_INVITE_REVIEW_VIEWED:
    '[Review guest invite] Invite page viewed' as const,
  GUEST_INVITE_REVIEW_TEAM_MEMBER_CHANGED:
    '[Review guest invite] Team member changed' as const,
  GUEST_INVITE_REVIEW_ROLE_CHANGED:
    '[Review guest invite] Role changed' as const,
  GUEST_INVITE_REVIEW_PUBLISH_PERMISSION_CHANGED:
    '[Review guest invite] Publish permission changed' as const,
  GUEST_INVITE_ACCEPTED: '[Review guest invite] Invite accepted' as const,
  CLIENT_MANAGEMENT_PAGE_VIEWED:
    '[Client management] Clients page viewed' as const,
  CLIENT_MANAGEMENT_ADD_CLIENT_VIEWED:
    '[Client management] Add client flow viewed' as const,
  CLIENT_MANAGEMENT_TEAM_MEMBER_CHANGED:
    '[Client management] Guest request team member changed' as const,
  CLIENT_MANAGEMENT_CLIENT_REQUEST_SENT:
    '[Client management] Add client request sent' as const,
  GUEST_REQUEST_REVIEW_VIEWED: '[Review guest request] Modal viewed' as const,
  GUEST_REQUEST_REVIEW_ROLE_CHANGED:
    '[Review guest request] Role changed' as const,
  GUEST_REQUEST_REVIEW_CAN_PUBLISH_CHANGED:
    '[Review guest request] Can publish changed' as const,
  GUEST_REQUEST_REVIEW_ADVANCED_PERMISSIONS_CHANGED:
    '[Review guest request] Advanced permission changed' as const,
  GUEST_REQUEST_ACCEPTED: '[Review guest request] Request accepted' as const,
  GUEST_TEAM_SUB_PAGE_PAGE_VIEWED: '[Guest team sub page] Page viewed' as const,
  GUEST_TEAM_SUB_PAGE_ROLE_CHANGED:
    '[Guest team sub page] Role changed' as const,
  ADD_GUEST_TEAM_MEMBER_MODAL_VIEWED:
    '[Add guest team member] Modal viewed' as const,
  ADD_GUEST_TEAM_MEMBER_TEAM_MEMBER_ADDED:
    '[Add guest team member] Team member added' as const,
  MANAGE_GUEST_MODAL_GUEST_SETTINGS_UPDATED:
    '[Manage guest modal] Guest settings updated' as const,
  HIRE_PAGE_VIEWED: 'Hire Page Viewed' as const,
  HOSTING_TRANSFERRED: 'Hosting Transferred' as const,
  IMAGE_ID_CHANGED: 'Designer Image Updated' as const,
  IDENTITY_CAPTURE_MODAL_OPENED: 'Identity Capture Modal Opened' as const,
  IDENTITY_CAPTURE_MODAL_FILLED: 'Identity Capture Modal Filled' as const,
  INTEGRATION_ERROR_VIEWED: 'Integration Error Viewed' as const,
  INTEGRATION_SETUP_COMPLETED: 'Integration Setup Completed' as const,
  INTEGRATION_SETUP_STARTED: 'Integration Setup Started' as const,
  INTEGRATION_TAB_VIEWED: 'Integration Tab Viewed' as const,
  INVENTORY_SYNC_CSV_REQUESTED: 'Inventory Sync CSV Requested' as const,
  INVOICE_CHARGE_FAILED: 'Invoice Charge Failed' as const,
  INVOICE_PAYMENT_SUCCEEDED: 'Invoice Payment Succeeded' as const,
  IN_APP_GUIDE_CTA_CLICKED: 'In-App Guide CTA Clicked' as const,
  IN_APP_MESSAGE_CTA_CLICKED: 'In-App Message CTA Clicked' as const,
  IN_APP_MESSAGE_DISMISSED: 'In-App Message Dismissed' as const,
  IN_APP_MESSAGE_VIEWED: 'In-App Message Viewed' as const,
  IN_APP_MESSAGE_EXPIRED: 'In-App Message Expired' as const,
  INLINE_BINDING_BOUND: 'Symbol Field Bound' as const,
  INLINE_BINDING_UNBOUND: 'Symbol Field Unbound' as const,
  INVITE_MODAL_REQUEST_SEATS_CLICKED:
    '[Invite modal] Request seats clicked' as const,
  INSIGHTS_SETTINGS_PANEL_OPENED: 'Insights Settings Opened' as const,
  INSIGHTS_TRACKING_TOGGLED: 'Insights Tracking Toggled' as const,
  INSIGHTS_TRACKING_DEFAULT_CHANGED:
    'Insights Tracking Defaults Edited' as const,
  INSIGHTS_ANALYZE_GOALS_INSIGHTS_OPENED: 'Analyze Goals Opened' as const,
  INSIGHTS_ANALYZE_CREATE_GOAL_CLICKED: 'Analyze Goals Promo' as const,
  INSIGHTS_ANALYZE_CSV_EXPORTED: 'Analyze CSV Exported' as const,
  INSIGHTS_ANALYZE_SWITCH_GOAL_WIDGET: 'Analyze Goals Widget Dropdown' as const,
  INSIGHTS_GOAL_REPORTING_OPENED: 'Analyze Goal Reporting Opened' as const,
  INSIGHTS_ANALYZE_SHOW_ALL_CLICKED: 'Analyze Show All Clicked' as const,
  INSIGHTS_ANALYZE_SITE_OVERVIEW_OPENED:
    'Analyze Site Overview Opened' as const,
  INSIGHTS_ANALYZE_PAGE_INSIGHTS_OPENED: 'Analyze Pages Opened' as const,
  INSIGHTS_ANALYZE_PAGE_INSIGHTS_DETAILS_OPENED:
    'Analyze Page Detail Opened' as const,
  INSIGHTS_ANALYZE_SWITCH_TO_ANALYZE_MODE: 'Analyze Switch to Analyze Mode',
  INSIGHTS_ANALYZE_FILTER_CHANGED: 'Analyze Page Filter Edited' as const,
  INSIGHTS_ANALYZE_UNIT_TOGGLED: 'Analyze Unit Toggled' as const,
  INSIGHTS_ANALYZE_VIEW_GRADIENT_LAYER_IN_ANALYZE_MODE:
    'Analyze View Gradient Layer in Analyze Mode' as const,
  // AEO Analytics Events
  ANALYZE_VIEW_TAB_CLICKED: 'Analyze View Tab Clicked' as const,
  ANALYZE_PROMPT_TAB_OPENED: 'Analyze Prompt Tab Opened' as const,
  ANALYZE_ADD_NEW_PROMPT: 'Analyze Add New Prompt' as const,
  ANALYZE_NEW_PROMPT_CREATED: 'Analyze New Prompt Created' as const,
  ANALYZE_PROMPT_DELETED: 'Analyze Prompt Deleted' as const,
  ANALYZE_PROMPT_DETAIL_OPENED: 'Analyze Prompt Detail Opened' as const,
  // Site Audit Analytics Events
  INSIGHTS_AEO_CONTENT_REVIEW_ACTION:
    'Insights AEO Content Review Action' as const,
  INSIGHTS_AEO_CONTENT_WORKFLOW_ACTION:
    'Insights AEO Content Workflow Action' as const,
  INSIGHTS_AEO_RECOMMENDATION_FEEDBACK_ACTION:
    'Insights AEO Recommendation Feedback Action' as const,
  INSIGHTS_AEO_SETTINGS_OPENED: 'Insights AEO Settings Opened' as const,
  INSIGHTS_AEO_SETTINGS_TOGGLED: 'Insights AEO Settings Toggled' as const,
  INSIGHTS_ORGANIZATION_INFO_OPENED:
    'Insights Organization Info Opened' as const,
  INSIGHTS_ORGANIZATION_INFO_SAVED: 'Insights Organization Info Saved' as const,
  INSIGHTS_SET_COMPETITORS_OPENED: 'Insights Set Competitors Opened' as const,
  INSIGHTS_SITE_AUDIT_OPENED: 'Insights Site Audit Opened' as const,
  INSIGHTS_SITE_AUDIT_CLICK: 'Insights Site Audit Click' as const,
  INSIGHTS_SITE_AUDIT_REVIEW_ACTION:
    'Insights Site Audit Review Action' as const,
  INSIGHTS_SITE_AUDIT_RUN: 'Insights Site Audit Run' as const,
  IPN_COLLISION: 'IPN_COLLISION' as const,
  IPN_DISMISSED: 'IPN_DISMISSED' as const,
  IPN_INTERACTED: 'IPN_INTERACTED' as const,
  IPN_VIEWED: 'IPN_VIEWED' as const,
  LANGUAGE_TARGET_PANEL_VIEWED: 'Language Target Panel Viewed' as const,
  LANGUAGE_TARGET_PANEL_CLICKED: 'Language Target Panel Clicked' as const,
  LANGUAGE_TARGET_UPSELL_VIEWED: 'Language Target Upsell Viewed' as const,
  LANGUAGE_TARGET_UPSELL_CTA_CLICKED:
    'Language Target Upsell CTA Clicked' as const,
  LEGACY_PLAN_CHANGE_MODAL_CLOSED: 'Legacy Plan Change Modal Closed' as const,
  LEGACY_PLAN_CHANGE_MODAL_OPENED: 'Legacy Plan Change Modal Opened' as const,
  LIBRARY_ELEMENT_BLOCKED: 'Library Element Blocked' as const,
  LIBRARY_ASSETS_BLOCKED: 'Library Assets Blocked' as const,
  LIBRARY_ACTION: 'Library Action' as const,
  LIBRARY_INSTALLED: 'Library Installed' as const,
  LIBRARY_ITEM_CTA_CLICKED: 'Library Item CTA Clicked' as const,
  LIBRARY_ORPHANED_CONTENT_DETECTED:
    'Library Orphaned Content Detected' as const,
  LIBRARY_ORPHANED_CONTENT_RECOVERED:
    'Library Orphaned Content Recovered' as const,
  LIBRARIES_MARKETPLACE_CTA_CLICKED: 'Marketplace CTA Clicked' as const,
  LIBRARIES_PANEL_FTUX_DISPLAYED: 'Libraries Panel FTUX Displayed' as const,
  LIBRARIES_PANEL_FTUX_CLICKED: 'Libraries Panel FTUX Clicked' as const,
  LIBRARIES_PANEL_OPENED: 'Libraries Panel Opened' as const,
  LOCALE_ADD_CLICKED: 'Locale Add Clicked' as const,
  LOCALE_CREATED: 'Locale Created' as const,
  LOCALE_UPDATED: 'Locale Updated' as const,
  LOCALE_DELETED: 'Locale Deleted' as const,
  LOCALE_CHANGES_DISCARDED: 'Locale Changes Discarded' as const,
  LOCALE_CLICKED: 'Locale Clicked' as const,
  LOCALE_CONTEXT_OPENED: 'Locale Context Opened' as const,
  LOCALE_CONTEXT_CLOSED: 'Locale Context Exited' as const,
  LOCALE_CONTEXT_SETTINGS_SELECTED: 'Locale Settings Selected' as const,
  LOCALE_PICKER_CLEAR_BUTTON_CLICKED:
    'Locale Picker Clear Button Clicked' as const,
  LOCALE_CONFIGURATION_OPENED: 'Locale Configuration Opened' as const,
  LOCALIZATION_UPSELL_RENDERED: 'Localization Upsell Popover Rendered' as const,
  LOCALIZATION_UPSELL_DISMISSED:
    'Localization Upsell Popover Dismissed' as const,
  LOCALIZATION_UPSELL_BUTTON_CLICKED:
    'Localization Upsell Popover CTA Clicked' as const,
  LOCALIZATION_DOM_CHANGED: 'Localization dom changed' as const,
  LOCALIZATION_SETTINGS_OPENED: 'Localization Settings Opened' as const,
  LOCALIZATION_SYMBOLS_CHANGED: 'Localization symbols changed' as const,
  LOCALIZATION_VALUE_RESET: 'Localization Value Reset' as const,
  OPEN_PROJECT_SELECTED: 'Open Project Selected' as const,
  OPTIMIZE_CONTEXT_OPENED: 'Optimize Context Opened' as const,
  OPTIMIZE_PICKER_CLEAR_BUTTON_CLICKED:
    'Optimize Picker Clear Button Clicked' as const,
  OPTIMIZE_CONTEXT_SELECTED: 'Optimize Context Selected' as const,
  OPTIMIZE_VARIANT_SELECTED: 'Optimize Variant Selected' as const,
  OPTIMIZE_CONTEXT_EXITED: 'Optimize Context Exited' as const,
  PAGE_FILTER_SELECTED_CLICKED: 'Page Filter Selected' as const,
  PAGE_SELECTED_CLICKED: 'Page Selected' as const,
  LOGIN_PAGE_VIEWED: 'Login Page Viewed' as const,
  MARKETER_COMPONENT_VISIBILITY_UPDATED:
    'Marketer Component Visibility Updated' as const,
  MARKETPLACE_APP_AUTHORIZED_TO_SITE:
    'Marketplace App Authorized to Site' as const,
  MARKETPLACE_APP_INSTALLED: 'Marketplace App Installed' as const,
  MARKETPLACE_APP_PLAN_SELECTED: 'Marketplace App Plan Selected' as const,
  MARKETPLACE_APP_SEARCH_EXECUTED: 'Marketplace - App Search Executed' as const,
  MARKETPLACE_APP_SEARCH_RESULT_CLICKED:
    'Marketplace - App Search Result Clicked' as const,
  MARKETPLACE_CATEGORY_CLICKED: 'Marketplace Category Clicked' as const,
  MARKETPLACE_CHECKOUT_CREATED: 'Marketplace Checkout Created' as const,
  MARKETPLACE_CHECKOUT_PAGE_VIEWED: 'Marketplace Checkout Page Viewed' as const,
  MARKETPLACE_CHECKOUT_REDIRECT_VIEWED:
    'Marketplace Checkout Redirect Page Viewed' as const,
  MARKETPLACE_CHECKOUT_REDIRECT_REDIRECTED:
    'Marketplace Checkout Redirected' as const,
  MARKETPLACE_CREATE_PROFILE_PAGE_VIEWED:
    'Marketplace Create Profile Page Viewed' as const,
  MARKETPLACE_CTA_CLICKED: 'Marketplace CTA Clicked' as const,
  MARKETPLACE_FILTERS_CHANGED: 'Marketplace Filters Changed' as const,
  MARKETPLACE_HUB_PAGE_VIEWED: 'Marketplace Hub Page Viewed' as const,
  MARKETPLACE_ORDER_CREATED: 'Marketplace Order Created' as const,
  MARKETPLACE_ORDER_TRANSITIONED: 'Marketplace Order Transitioned' as const,
  MARKETPLACE_PAYOUT_METHOD_CREATED:
    'Marketplace Payout Method Created' as const,
  MARKETPLACE_PAYOUT_METHOD_LOGIN_LINK_CREATED:
    'Marketplace Payout Method Login Link Created' as const,
  MARKETPLACE_PROFILE_CONTACT_EMAIL_SENT:
    'Marketplace Profile Contact Email Sent' as const,
  MARKETPLACE_RECOMMENDATION_CLICK:
    'Marketplace - Recommendation Click' as const,
  MARKETPLACE_RECOMMENDATION_IMPRESSION:
    'Marketplace - Recommendation Impression' as const,

  MARKETPLACE_SUBSCRIPTION_CREATED: 'Marketplace Subscription Created' as const,
  MARKETPLACE_SUBSCRIPTION_TRANSITIONED:
    'Marketplace Subscription Transitioned' as const,
  MARKETPLACE_VIEWED: 'Marketplace Viewed' as const,
  MATCHMAKING_EXPERTS_SURVEY_COMPLETED:
    'Matchmaking Experts Survey Completed' as const,
  MATCHMAKING_EXPERTS_SURVEY_ABANDONED:
    'Matchmaking Experts Survey Abandoned' as const,
  MATCHMAKING_EXPERTS_SURVEY_RESUMED:
    'Matchmaking Experts Survey Resumed' as const,
  MATCHMAKING_EXPERTS_SURVEY_STEP_SUBMITTED:
    'Matchmaking Experts Survey Step Submitted' as const,
  MATCHMAKING_EXPERTS_SURVEY_PAGE_VIEWED:
    'Matchmaking Experts Survey Page Viewed' as const,
  MATCHMAKING_EXPERTS_SURVEY_STEP_VIEWED:
    'Matchmaking Experts Survey Step Viewed' as const,
  MATCHMAKING_EXPERTS_SURVEY_ERROR_PAGE_VIEWED:
    'Matchmaking Experts Error Page Viewed' as const,
  MATCHMAKING_EXPERTS_SURVEY_ERROR_PAGE_CTA_CLICKED:
    'Matchmaking Error Note CTA Clicked' as const,
  MATCHMAKING_NOTE_CTA_CLICKED: 'Matchmaking Note CTA Clicked' as const,
  MATCHMAKING_NOTE_DISMISSED: 'Matchmaking Note Dismissed' as const,
  MATCHMAKING_NOTE_VIEWED: 'Matchmaking Note Viewed' as const,
  MATCHMAKING_ERROR_PAGE_VIEWED: 'Matchmaking Error Page Viewed' as const,
  MEMBER_SUBSCRIPTION_CREATED: 'Member Subscription Created' as const,
  MEMBER_SUBSCRIPTION_CANCELED: 'Member Subscription Canceled' as const,
  MENU_SECTION_TOGGLED: 'Menu Section Toggled' as const,
  MOBILE_ONBOARDING_PAGE_VIEWED: 'Mobile Onboarding Page Viewed' as const,
  MODAL_CLOSED: 'Modal Closed' as const,
  MODAL_OPENED: 'Modal Opened' as const,
  MODAL_VIEWED: 'Modal Viewed' as const,
  MORE_ACTIONS_CLICKED: 'More Actions Clicked' as const,
  NINE_BOX_FLEX_CLICKED: 'Nine Box Flex Clicked' as const,
  NINE_BOX_GRID_CLICKED: 'Nine Box Grid Clicked' as const,
  NEW_PROJECT_PAGE_VIEWED: 'New Project Page Viewed' as const,
  NEW_PROJECT_PAGE_BACK: 'New Project Page Back' as const,
  NODE_CLICKED: 'Node Clicked' as const,
  ONBOARDING_FOLLOWUP_ACTION_SELECTED:
    'Onboarding Followup Action Selected' as const,
  OFFLINE_CONVERSION_TOKEN_COPIED: 'Offline Conversion Token Copied' as const,
  OFFLINE_CONVERSION_TOKEN_CREATED: 'Offline Conversion Token Created' as const,
  OFFLINE_CONVERSION_TOKEN_DELETED: 'Offline Conversion Token Deleted' as const,
  ONBOARDING_PREFERENCES_EDITED: 'Onboarding Preferences Edited' as const,
  ONBOARDING_TUTORIAL_DISMISSED: 'Onboarding Tutorial Dismissed' as const,
  ONBOARDING_TUTORIAL_REPEATED: 'Onboarding Tutorial Repeated' as const,
  ONBOARDING_TUTORIAL_CONTINUED: 'Onboarding Tutorial Continued' as const,
  ONBOARDING_TUTORIAL_EXIT_ATTEMPTED:
    'Onboarding Tutorial Exit Attempted' as const,
  ONBOARDING_TUTORIAL_SELECTED: 'Onboarding Tutorial Selected' as const,
  OVERAGE_NOTIFICATION_VIEWED: 'Overage Notification Viewed' as const,
  OVERAGE_NOTIFICATION_UPGRADE_CTA_CLICKED:
    'Overage Notification Upgrade Now Clicked' as const,
  OVERAGE_NOTIFICATION_SEE_USAGE_CTA_CLICKED:
    'Overage Notification See Usage Clicked' as const,
  OVERAGE_NOTIFICATION_GO_TO_BILLING_CTA_CLICKED:
    'Overage Notification Go To Billing Clicked' as const,
  OVERAGE_NOTIFICATION_LEARN_MORE_CTA_CLICKED:
    'Overage Notification Learn more Clicked' as const,
  OVERAGE_NOTIFICATION_CONTACT_SALES_CTA_CLICKED:
    'Overage Notification Contact Sales Clicked' as const,
  OVERAGE_NOTIFICATION_BADGE_CLICKED:
    'Overage Notification Badge Clicked' as const,
  OVERAGE_NOTIFICATION_CLOSED: 'Overage Notification Closed' as const,
  OVERAGE_NOTIFICATION_SENT: 'Overage Notification Sent' as const,
  PAGES_PANEL_VIEWED: 'Pages Panel Viewed' as const,
  PAGE_SETTINGS_OPENED: 'Page Settings Opened' as const,
  PARTNER_HUB_AVAILABILITY_STATUS_CLICKED:
    'Partner Hub Availability Status Clicked' as const,
  PARTNER_HUB_CERTIFIED_PARTNER_APPLICATION_VIEWED:
    'Partner Hub Certified Partner Application Viewed' as const,
  PARTNER_HUB_CERTIFIED_PARTNER_APPLICATION_SUBMITTED:
    'Partner Hub Certified Partner Application Submitted' as const,
  PARTNER_HUB_CODE_COPIED: 'Partner Hub Code Copied' as const,
  PARTNER_HUB_COMMISSION_EXPLAINER_OPENED:
    'Partner Hub Commission Explainer Opened' as const,
  PARTNER_HUB_FAQ_OPENED: 'Partner Hub FAQ Opened' as const,
  PARTNER_HUB_ONBOARDING_STEP_COMPLETED:
    'Partner Hub Onboarding Step Completed' as const,
  PARTNER_HUB_STEP_WAIVED: 'Partner Hub Step Waived' as const,
  PARTNER_HUB_PAGE_VIEWED: 'Partner Hub Page Viewed' as const,
  PARTNER_PRE_QUALIFICATION_EXAM_VIEWED:
    'Partner Pre-qualification Exam Viewed' as const,
  PARTNER_PRE_QUALIFICATION_EXAM_SUBMITTED:
    'Partner Pre-qualification Exam Submitted' as const,
  PARTNER_HUB_RESOURCE_VIEWED: 'Partner Hub Resource Viewed' as const,
  PARTNER_HUB_SUBMIT_ENTERPRISE_LEAD_CLICKED:
    'Partner Hub Submit Enterprise Lead Clicked' as const,
  PARTNER_SANDBOX_CTA_CLICKED: 'Partner Sandbox CTA Clicked' as const,
  PASSWORD_RESET_INSTRUCTIONS_SENT: 'Password Reset Instructions Sent' as const,
  PASSWORD_RESET_FAILED: 'Password Reset Failed' as const,
  PASSWORD_RESET_SUCCESS: 'Password Reset Success' as const,
  PAYMENT_AUTH_COMPLETED: 'Required Payment Authentication Completed' as const,
  PAYMENT_AUTH_FAILED: 'Required Payment Authentication Failed' as const,
  PAYMENT_AUTH_STARTED: 'Required Payment Authentication Started' as const,
  PAYMENT_AUTH_VIEWED: 'Show Required Payment Authentication Notice' as const,
  PAYMENT_FAILED: 'Payment Failed' as const,
  PAYMENT_METHOD_ADD_CLICK: 'Add Payment Method Clicked' as const,
  PAYMENT_METHOD_REMOVE_CLICK: 'Remove Payment Method Clicked' as const,
  PAYMENT_METHOD_REMOVE_CONFIRMED: 'Remove Payment Method Confirmed' as const,
  PAYMENT_METHOD_REMOVE_CANCELLED: 'Remove Payment Method Cancelled' as const,
  PAYMENT_METHOD_UPDATE_CLICK: 'Update Payment Method Clicked' as const,
  PAYMENT_METHOD_UPDATE_CONFIRMED:
    '[Client payments] Update Payment Method Confirmed' as const,
  PAYMENT_METHOD_UPDATE_ERROR: 'Credit card charge failed' as const,
  PAYMENT_REQUEST_SENT: '[Client payments] Payment Request Sent' as const,
  PAYMENT_SUCCESS: 'Payment Success' as const,
  PAYWALL_ENCOUNTERED: 'Paywall Encountered' as const,
  PERFORMANCE_NOTIFICATION_VIEWED: 'Performance Notification Viewed' as const,
  PERSON_PROFILE_CREATED: 'Person profile created' as const,
  PLAN_MODIFIED: 'Plan Modified' as const,
  PLAN_OBJECT_TAB_PRESSED: 'Plan Object Tab Pressed' as const,
  PLAN_RECOMMENDATION: 'Plan Recommendation' as const,
  PLAN_RECOMMENDATION_CLICKED: 'Plan Recommendation Clicked' as const,
  PLAN_RECOMMENDATION_DISMISSED: 'Plan Recommendation Dismissed' as const,
  PLAN_SEATS_UPDATED: 'Plan Seats Updated' as const,
  PLAN_UPGRADED_BY_BUSINESS: 'Plan Upgraded by Business' as const,
  PLAN_UPGRADED_BY_COMPANY: 'Plan Upgraded by Company' as const,
  PLAN_UPGRADED_BY_SELF: 'Plan Upgraded by Self' as const,
  PLAN_UPGRADED_BY_SP: 'Plan Upgraded by SP' as const,
  PLAN_UPGRADED_FROM_FREE: 'Plan Upgraded from Free' as const,
  PLANS_PAGE_VIEWED: 'Plans Page Viewed' as const,
  PRICING_CTA_CLICKED: 'Pricing CTA Clicked' as const,
  PROFILE_CARD_CLICKED: 'Profile Card Clicked' as const,
  PROFILE_SETTINGS_PAGE_VIEWED: 'Profile Settings Page Viewed' as const,
  PROJECT_CREATED: 'Project Created' as const,
  PROJECT_DELETED: 'Project Deleted' as const,
  PROJECT_FILTER_SELECTED: 'Project Filter Selected' as const,
  PROJECT_PUBLISHED: 'Project Published' as const,
  PROJECT_SHOWCASED: 'Project Showcased' as const,
  PROJECT_SNAPSHOT_RESTORED: 'Project Snapshot Restored' as const,
  PROJECT_TRANSFERRED: 'Project Transferred' as const,
  PROJECT_TRANSFER_ACCEPTED: 'Project Transfer Accepted' as const,
  PROJECT_TRANSFER_RECEIVED: 'Project Transfer Received' as const,
  PROJECT_LINK_COPIED: 'Project Link Copied' as const,
  PROJECT_LINK_UPDATED: 'Project Link Updated' as const,
  PUBLISHING_WORKFLOWS_PUBLISH_CLICKED:
    'Publishing Workflows Publish Clicked' as const,
  PUBLISHING_WORKFLOWS_PUBLISH_SCOPE_TOGGLE_CLICKED:
    'Publishing Workflows Publish Scope Toggle Clicked' as const,
  CHANGE_SUMMARY_CHANGES_EXPANDED: 'Change Summary Changes Expanded' as const,
  CHANGE_SUMMARY_CHANGE_DETAILS_CLICKED:
    'Change Summary Change Details Clicked' as const,
  QUICK_ADD_CTA_CLICKED: 'Quick Add CTA Clicked' as const,
  QUICK_ADD_EMPTY_SECTION_STATE_VIEWED:
    'Quick Add Empty Section State Viewed' as const,
  QUICK_ADD_MODAL_OPENED: 'Quick Add Modal Opened' as const,
  QUICK_ADD_ONBOARDING_PLUS_BUTTON_VIEWED:
    'Quick Add Onboarding Plus Button Viewed',
  QUICK_FIND_MODAL_CLOSED: '[Dashboard Quick find] Modal closed' as const,
  QUICK_FIND_MODAL_OPENED: '[Dashboard Quick find] Modal opened' as const,
  QUICK_FIND_SEARCH_EXECUTED: '[Dashboard Quick find] Search executed' as const,
  QUICK_FIND_SEARCH_RESULT_CLICKED:
    '[Dashboard Quick find] Search result clicked' as const,
  QUICK_STACK_EDIT_MODE_ENTERED: 'Quick Stack Edit Mode Entered' as const,
  QUICK_STACK_UPDATED: 'Quick Stack Updated' as const,
  REALTIME_ONBOARDING_VIEWED: 'Realtime Onboarding Viewed' as const,
  ORION_SYNC_STYLES_TRIGGERED: 'Orion Sync Styles Triggered' as const,
  ORION_ADD_CONTEXT_POPOVER_TRIGGERED:
    'Orion Add Context Popover Triggered' as const,
  REDIRECT_ADDED: 'Added redirect' as const,
  REDIRECT_EXPORT_BUTTON_CLICKED: 'Redirect Export Button Clicked' as const,
  REDIRECT_IMPORT_BUTTON_CLICKED: 'Redirect Import Button Clicked' as const,
  REDIRECT_IMPORT_MODAL_CLOSED: 'Redirect Import Modal Closed' as const,
  REDIRECT_IMPORT_MODAL_OPENED: 'Redirect Import Modal Opened' as const,
  REDO_TRIGGERED: 'Redo triggered' as const,
  REMOVED_ALL_REDIRECTS: 'Removed all redirects' as const,
  RENAME_COLLECTION_SLUG: 'Rename Collection Slug' as const,
  REQUEST_PAYMENT_PAGE_VIEWED:
    '[Client payments] Request Payment Page Viewed' as const,
  RICH_TEXT_CONTENT_PASTED: 'Rich text content pasted' as const,
  SAFARI_17_2_DETECTED: 'Safari 17.2 notification displayed' as const,
  SANDBOX_LINK_COPIED: 'Sandbox Link Copied' as const,
  SANDBOX_LINK_UPDATED: 'Sandbox Link Updated' as const,
  SECONDARY_EMAIL_REMOVAL_STARTED: 'Secondary Email Removal Started' as const,
  SECONDARY_EMAIL_REMOVAL_COMPLETED:
    'Secondary Email Removal Completed' as const,
  SECONDARY_EMAIL_REMOVED: 'Removed Secondary Email' as const,
  SECONDARY_EMAIL_VERIFICATION_COMPLETED:
    'Secondary Email Verification Completed' as const,
  SECONDARY_EMAIL_VERIFIED: 'Verified Secondary Email' as const,
  SECONDARY_TUTORIAL_SELECTED: 'Secondary Tutorial Selected' as const,
  SEAT_PURCHASE_INTENT_SHOWN: 'Seat Purchase Intent Shown' as const,
  SEGMENTATION_SURVEY_COMPLETED: 'Segmentation Survey Completed' as const,
  SEGMENTATION_SURVEY_PAGE_VIEWED: 'Segmentation Survey Page Viewed' as const,
  SEGMENTATION_SURVEY_STEP_SUBMITTED:
    'Segmentation Survey Step Submitted' as const,
  SEO_IMAGE_ALT_TEXT_ACCEPTED: 'SEO Image Alt Text Accepted' as const,
  SEO_IMAGE_ALT_TEXT_GENERATED: 'SEO Image Alt Text Generated' as const,
  SEO_IMAGE_ALT_TEXT_UNDONE: 'SEO Image Alt Text Undone' as const,
  SEO_IMAGE_ALT_TEXT_UPDATED: 'SEO Image Alt Text Updated' as const,
  SEO_PAGE_SETTINGS_FIELD_USER_OVERRIDDEN:
    'SEO Page Settings Field User Overridden' as const,
  SEO_PAGE_SETTINGS_GENERATED: 'SEO Page Settings Generated' as const,
  SERVICE_PROVIDER_CONFIRMATION_ACCEPTED:
    'Service Provider Confirmation Accepted' as const,
  SERVICE_PROVIDER_CONFIRMATION_UPDATED:
    'Service Provider Confirmation Updated' as const,
  SERVICE_PROVIDER_CONFIRMATION_DISMISSED:
    'Service Provider Confirmation Dismissed' as const,
  SERVICE_PROVIDER_CONFIRMATION_VIEWED:
    'Service Provider Confirmation Viewed' as const,
  SESSION_REPLAY_CONSENT: 'Session Replay Consent' as const,
  SESSION_REPLAY_CONSENT_MODAL_OPENED:
    'Session Replay Consent Modal Opened' as const,
  SHOWCASE_ACTION_RECEIVED: 'Showcase Action Received' as const,
  SHOWCASE_DIGEST_GENERATED: 'Showcase Digest Generated' as const,
  SHOWCASE_VIEWED: 'Showcase Viewed' as const,
  SIGN_UP: 'Sign Up' as const,
  SIGN_UP_PAGE_VIEWED: 'Sign Up Page Viewed' as const,
  SIGN_UP_PAGE_CUSTOMER_STORIES_VIEWED:
    'Sign Up Page Customer Stories Viewed' as const,
  SINGLE_ITEM_PUBLISHED: 'Single Item Published' as const,
  SINCE_YOU_BEEN_GONE_MODAL_CLOSED: 'Since You Been Gone Modal Closed' as const,
  SINCE_YOU_BEEN_GONE_MODAL_VIEWED: 'Since You Been Gone Modal Viewed' as const,
  STATUS_ITEM_CLICKED: 'Status Item Clicked' as const,
  STATUS_MENU_CLICKED: 'Status Menu Clicked' as const,
  VIEW_ALL_CLICKED: 'View All Clicked' as const,
  EDIT_CUSTOM_CODE: 'Edit Custom Code' as const,
  SAVE_CUSTOM_CODE: 'Save Custom Code' as const,
  PREVIEW_CUSTOM_CODE: 'Preview Custom Code' as const,
  PREVIEW_CUSTOM_CODE_PAGE_LOADED: 'Preview Custom Code Page Loaded' as const,
  PREVIEW_ROLE_OPTION_SELECTED: 'Preview Role Option Selected' as const,
  SEAMLESS_SITE_TRANSFER_REQUESTED:
    '[Client payments] Site Transfer Requested' as const,
  SEAMLESS_SITE_TRANSFER_ACCEPTED:
    '[Client payments] Site Transfer Accepted' as const,
  SHARE_SITE_CLICKED: 'Share site clicked' as const,
  SITE_EXPORT_QUEUED: 'Site Export Queued' as const,
  SITE_EXPORT_REQUESTED: 'Site Export Requested' as const,
  SITE_INTEGRATIONS_UPDATED: 'Site Integrations Settings Updated' as const,
  SITE_INSIGHTS_UPDATED: 'Site Insights Settings Updated' as const,
  SITE_ROLE_TOGGLED: 'Site role toggled' as const,
  SITE_IN_SITE_SPECIFIC_ACCESS_WORKSPACE_CREATED:
    'Site In Site Specific Access Workspace Created' as const,
  SITE_PLANS_PAGE_VIEWED: 'Site Plans Page Viewed' as const,
  SITE_PLAN_CHARGE_TO_CLIENT_CLICKED:
    '[Client payments] Site Plan Charge To Client Clicked' as const,
  SITE_PLAN_PAY_ON_OWN_CLICKED:
    '[Client payments] Site Plan Pay On Your Own Clicked' as const,
  SITE_RECOMMENDATION_CTA_CLICKED: 'Site Recommendation CTA Clicked' as const,
  SITE_SEO_SETTINGS_UPDATED: 'Site SEO Settings Updated' as const,
  SITE_SETTINGS_PAGE_VIEWED: 'Site Settings Page Viewed' as const,
  SITE_SETTINGS_OPEN_DESIGNER_CLICKED: 'Open Webflow App Clicked' as const,
  SITE_SETTINGS_OPEN_EDITOR_CLICKED: 'Open Editor Clicked' as const,
  SITE_SETTINGS_PUBLISH_CLICKED: 'Site Settings Publish Clicked' as const,
  SITE_SETTINGS_UNPUBLISH_CLICKED: 'Site Settings Unpublish Clicked' as const,
  SITE_SPECIFIC_ACCESS_BULK_VALUE_UPDATED:
    'Specific Access Bulk Value Updated' as const,
  SITE_SPECIFIC_ACCESS_SITE_MEMBERS_ADDED:
    'Site Specific Access Site Members Added' as const,
  SITE_SPECIFIC_ACCESS_SITE_MEMBERS_REMOVED:
    'Site Specific Access Site Members Removed' as const,
  SITE_SPECIFIC_ACCESS_VALUE_UPDATED:
    'Site Specific Access Value Updated' as const,
  SITE_TRANSFER_CANCELED: 'Site Transfer Canceled' as const,
  SITE_TRANSFER_MODAL_CANCELED: 'Site Transfer Modal Canceled' as const,
  SITE_TRANSFER_MODAL_OPENED: 'Site Transfer Modal Opened' as const,
  SITE_TRANSFER_MODAL_SUBMITTED: 'Site Transfer Modal Submitted' as const,
  SITE_TRANSFER_RECEIVED_ACCEPTED: 'Site Transfer Received Accepted' as const,
  SITE_TRANSFER_REQUEST_ACCEPTED: 'Site Transfer Request Accepted' as const,
  SITE_TRANSFER_REQUEST_RECEIVED: 'Site Transfer Request Received' as const,
  SITE_TRANSFER_REQUEST_SENT: 'Site Transfer Request Sent' as const,
  SLOT_RESTRICTION_ENFORCED: 'Slot Restriction Enforced' as const,
  SLOT_RESTRICTION_SPECIFIED: 'Slot Restriction Specified' as const,
  SSO_LOGIN_PAGE_VIEWED: 'SSO Login Page Viewed' as const,
  STRIPE_METHOD: 'Stripe Method Called' as const,
  SUGGESTED_PROPS_REVIEWED: 'Suggested Props Reviewed' as const,
  SURVEY_MODAL_OPENED: 'Survey Modal Opened' as const,
  SURVEY_MODAL_CLOSED: 'Survey Modal Closed' as const,
  SURVEY_SUBMITTED: 'Survey Submitted' as const,
  SWITCH_TO_DESIGN_MODE_CLICKED: 'Switch to Design Mode Clicked' as const,
  SWITCH_TO_EDIT_MODE_CLICKED: 'Switch to Edit Mode Clicked' as const,
  SWITCH_TO_BUILD_MODE_CLICKED: 'Switch to Build Mode Clicked' as const,
  SWITCH_TO_VIEW_MODE_CLICKED: 'Switch to View Mode Clicked' as const,
  SYMBOL_CREATED: 'Symbol Created' as const,
  SYMBOL_OPENED: 'Symbol Opened' as const,
  SYMBOL_UNLINKED: 'Symbol Unlinked' as const,
  TAB_PRESSED: 'Tab Pressed' as const,
  TEMPLATE_ITEM_CTA_CLICKED: 'Template Item CTA Clicked' as const,
  TEMPLATE_PURCHASE_OPEN: 'Template Purchase Open' as const,
  TEMPLATE_MODAL_PURCHASED_CHECKED: 'Template Modal Purchased Checked' as const,
  TEMPLATE_MODAL_SELECTED: 'Template Modal Selected' as const,
  TEMPLATE_SELECTED: 'Template Selected' as const,
  TEMPLATE_PURCHASED: 'Template Purchased' as const,
  TOGGLE_FRAME_CONTENT_VISIBILITY: 'Toggle Frame Content Visibility' as const,
  TOGGLED_BOT_PROTECTION: 'Toggled Bot Protection' as const,
  TOGGLED_HIDE_BRANDING: 'Toggled Hide Branding' as const,
  TOGGLED_SHOW_WEBFLOW_BADGE: 'Toggled Webflow badge' as const,
  TOOLTIP_OPENED: 'Tooltip Opened' as const,
  TOOLTIP_CLOSED: 'Tooltip Closed' as const,
  TRIAL_STARTED: 'Trial Started' as const,
  TRY_IT_CLICKED: 'Try it Button Clicked' as const,
  TUTORIAL_LINK_CLICKED: 'Tutorial Link Clicked' as const,
  TUTORIAL_SECTION_DISMISSED: 'Tutorial Section Dismissed' as const,
  TUTORIAL_SECTION_MINIMIZED: 'Tutorial Section Minimized' as const,
  TUTORIAL_SECTION_VIEWED: 'Tutorial Section Viewed' as const,
  TWO_FACTOR_AUTH_PAGE_VIEWED: '2FA Page Viewed' as const,
  UNDO_TRIGGERED: 'Undo triggered' as const,
  UNIVERSITY_CERTIFICATION_PASSED: 'University Certification Passed' as const,
  UNIVERSITY_CERTIFICATION_FAILED: 'University Certification Failed' as const,
  UNIVERSITY_EXAM_FAILED: 'University Exam Failed' as const,
  UNIVERSITY_EXAM_PASSED: 'University Exam Passed' as const,
  UPDATED_FORMS: 'Updated Forms' as const,
  UPDATED_SITE_SETTINGS: 'Updated Site Settings' as const,
  UPGRADE_MODAL_CLOSED: 'Upgrade Modal Closed' as const,
  UPGRADE_MODAL_OPENED: 'Upgrade Modal Opened' as const,
  UPGRADE_MODAL_WORKSPACE_SELECTED: 'Upgrade Modal Workspace Selected' as const,
  UPGRADED_PLAN: 'Upgraded plan' as const,
  USER_SYSTEMS_ENABLED: 'User Systems Enabled' as const,
  USER_SYSTEMS_USER_CSV_IMPORTED: 'User Systems User CSV Imported' as const,
  VARIANT_CREATED: 'Variant Created' as const,
  VIDEO_PLAYED: 'Video played' as const,
  VIDEO_TUTORIAL_PLAYED: 'Video tutorial played' as const,
  VIDEO_TUTORIAL_SELECTED: 'Video tutorial selected' as const,
  VIDEO_TUTORIALS_PANEL_VIEWED: 'Video Tutorials Panel Viewed' as const,
  VIEW_ALL_PLANS_CLICKED: 'View All Plans Clicked' as const,
  VIDEO_TUTORIALS_PLAYER_RESIZED: 'Video Tutorials Player Resized' as const,
  NEW_COMPONENT_MENU_OPENED: 'New Component Menu Opened' as const,
  VCC_BUILD_FINISHED: 'VCC Build Finished' as const,
  VCC_BUILT_FROM_SCRATCH: 'VCC Built from Scratch' as const,
  // Pending Segment tracking-plan + `typewriter update && typewriter build` —
  // cast as EventName at call sites until types regenerate.
  VCC_CLARIFICATION_ANSWERED: 'VCC Clarification Answered' as const,
  VCC_CLARIFICATION_GENERATION_COMPLETED:
    'VCC Clarification Generation Completed' as const,
  VCC_CLARIFICATION_QUESTION_SHOWN: 'VCC Clarification Question Shown' as const,
  VCC_CLARIFICATION_REGENERATED: 'VCC Clarification Regenerated' as const,
  VCC_CLARIFICATION_STARTED: 'VCC Clarification Started' as const,
  VCC_COMPONENT_SAVED: 'VCC Component Saved' as const,
  VCC_COMPONENT_USED: 'VCC Component Used' as const,
  VCC_EDIT_STARTED: 'VCC Edit Started' as const,
  VCC_ELEMENT_SELECTED: 'VCC Element Selected' as const,
  VCC_FLOW_STARTED: 'VCC Flow Started' as const,
  VCC_PREVIEWED: 'VCC Previewed' as const,
  VCC_PROMPT_ENTERED: 'VCC Prompt Entered' as const,
  VCC_PUBLISHED: 'VCC Published' as const,
  VCC_STATES_UPDATED: 'VCC States Updated' as const,
  VCC_REAL_EDIT: 'VCC Real Edit' as const,
  // EXT-1963 — multifile source-file operations. Tracking-plan schemas still
  // pending in segment-tracking-plan-management; typed via DesignerLegacyEvents
  // until typewriter regen.
  VCC_SOURCE_FILE_CREATED: 'VCC Source File Created' as const,
  VCC_SOURCE_FILE_DELETED: 'VCC Source File Deleted' as const,
  VCC_SOURCE_FILE_RENAMED: 'VCC Source File Renamed' as const,
  VCC_SOURCE_FILE_SAVED: 'VCC Source File Saved' as const,
  // EXT-2403: refused before the write, so the server's own rejection counter
  // stops seeing them. Schemas pending in segment-tracking-plan-management, as
  // above.
  VCC_SOURCE_FILE_SAVE_REFUSED: 'VCC Source File Save Refused' as const,
  // EXT-2359: schemas pending in segment-tracking-plan-management.
  VCC_START_FROM_CODE_CLICKED: 'VCC Start From Code Clicked' as const,
  VCC_PASTE_HARNESS_APPLIED: 'VCC Paste Harness Applied' as const,
  VCC_STYLE_FIELD_UPDATED: 'VCC Style Field Updated' as const,
  VCC_SUGGESTION_USED: 'VCC Suggestion Used' as const,
  VCC_TEXT_EDITED: 'VCC Text Edited' as const,
  VCC_ENTRYPOINT_VIEWED: 'VCC Entrypoint Viewed' as const,
  VCC_ENTRYPOINT_CLICKED: 'VCC Entrypoint Clicked' as const,
  VCC_ENTRYPOINT_DISMISSED: 'VCC Entrypoint Dismissed' as const,
  VCC_UPGRADE_CTA_CLICKED: 'VCC Upgrade CTA Clicked' as const,
  // EXT-2393 — cross-state draft cascade. Tracking-plan schema still pending.
  VCC_VISUAL_EDIT_CASCADED: 'VCC Visual Edit Cascaded' as const,
  VCC_VISUAL_EDIT_DISCARDED: 'VCC Visual Edit Discarded' as const,
  VCC_VISUAL_EDIT_SUBMITTED: 'VCC Visual Edit Submitted' as const,
  VCC_VISUAL_EDIT_SURFACE_SHOWN: 'VCC Visual Edit Surface Shown' as const,
  VCC_VIEW_ASSISTANT_CHAT_CLICKED: 'VCC View Assistant Chat Clicked' as const,
  // Tracking-plan PR: https://github.com/webflow/segment-tracking-plan-management/pull/266
  // (LIF-346). Pending merge + `typewriter update && typewriter build` regen —
  // these keys are not yet valid `EventName` values, see that PR for status.
  WAF_RULE_DELETED: 'WAF Rule Deleted' as const,
  WAF_RULE_SAVED: 'WAF Rule Saved' as const,
  WAF_VERIFIED_BOTS_TOGGLED: 'WAF Verified Bots Toggled' as const,
  WORDS_TRANSLATED: 'Words Translated' as const,
  WORKFLOW_ENTERING_CMS: 'CMS Tab Opened' as const,
  WORKFLOW_LEAVING_CMS: 'CMS Tab Closed' as const,
  WORKFLOW_ENTERING_DESIGN: 'Canvas Revealed' as const,
  WORKFLOW_LEAVING_DESIGN: 'Canvas Mode Closed' as const,
  WORKFLOW_ENTERING_PREVIEW: 'Project Previewed' as const,
  WORKFLOW_LEAVING_PREVIEW: 'Project Preview Closed' as const,
  WORKFLOW_ENTERING_ANALYZE_AND_OPTIMIZE:
    'Analyze & Optimize Tab Opened' as const,
  WORKFLOW_LEAVING_ANALYZE_AND_OPTIMIZE:
    'Analyze & Optimize Tab Closed' as const,
  WORKFLOW_ENTERING_OPTIMIZE_CLICK_GOAL_MODE:
    'Entering Optimization Click Goal Mode' as const,
  WORKFLOW_LEAVING_OPTIMIZE_CLICK_GOAL_MODE:
    'Exiting Optimization Click Goal' as const,
  WORKFLOW_ENTERING_OPTIMIZE_REDIRECT_TEST_MODE:
    'Entering Optimization Redirect Test Mode' as const,
  WORKFLOW_LEAVING_OPTIMIZE_REDIRECT_TEST_MODE:
    'Exiting Optimization Redirect Test' as const,
  WORKFLOW_ENTERING_ANNOTATIONS_MODE: 'Comment Mode Opened' as const,
  WORKFLOW_LEAVING_ANNOTATIONS_MODE: 'Comment Mode Closed' as const,
  WORKFLOW_ENTERING_ANALYZE_MODE: 'Analyze Mode Opened' as const,
  WORKFLOW_LEAVING_ANALYZE_MODE: 'Analyze Mode Closed' as const,
  WORKFLOW_ENTERING_LOGIC_FLOWS: 'Logic Flows Opened' as const,
  WORKFLOW_LEAVING_LOGIC_FLOWS: 'Logic Flows Closed' as const,
  WORKFLOW_ENTERING_APP_GEN: 'App Gen Opened' as const,
  WORKFLOW_LEAVING_APP_GEN: 'App Gen Closed' as const,
  WORKSPACE_ACCESS_REQUEST_ACTIONED:
    'Workspace Access Request Actioned' as const,
  WORKSPACE_ACCESS_REQUEST_CLICKED: 'Workspace Access Request Clicked' as const,
  WORKSPACE_ACCESS_REQUEST_AUTH_REDIRECTED:
    'Workspace Access Request Auth Redirected' as const,
  WORKSPACE_ACCESS_REQUEST_AUTH_SUCCEEDED:
    'Workspace Access Request Auth Succeeded' as const,
  WORKSPACE_ACCESS_REQUEST_FAILED: 'Workspace Access Request Failed' as const,
  WORKSPACE_ACCESS_REQUEST_SUCCEEDED:
    'Workspace Access Request Succeeded' as const,
  WORKSPACE_ACCESS_REQUEST_VIEWED: 'Workspace Access Request Viewed' as const,
  WORKSPACE_AI_TOGGLE_CLICKED: 'Workspace AI Toggle Clicked' as const,
  WORKSPACE_BILLING_PAGE_VIEWED: 'Workspace Billing Page Viewed' as const,
  WORKSPACE_COMMENTER_SIGNUP_SUCCEEDED: 'Workspace Reviewer Signup' as const,
  WORKSPACE_IMAGE_CHANGED: 'Workspace Profile Image Updated' as const,
  WORKSPACE_INVITATION_ACCEPTED: 'Workspace Invitation Accepted' as const,
  WORKSPACE_INVITATION_CANCELED: 'Workspace Invitation Canceled' as const,
  WORKSPACE_INVITATION_DECLINED: 'Workspace Invitation Declined' as const,
  WORKSPACE_INVITATION_SENT: 'Workspace Invitation Sent' as const,
  WORKSPACE_MEMBER_REMOVED: 'Workspace Member Removed' as const,
  WORKSPACE_PROFILE_CREATED: 'Workspace profile created' as const,
  WORKSPACE_FRICTIONLESS_INVITE_PURCHASED:
    'Workspace Frictionless Invite Purchased' as const,
  WORKSPACE_SETTINGS_PAGE_VIEWED: 'Workspace Settings Page Viewed' as const,
  WORKSPACE_PAGE_PLAN_VIEWED: 'Workspace Plan Page Viewed' as const,
  WORKSPACE_PRICING_UPDATE_NOTIFICATION_SENT:
    'Pricing Update Notification Sent' as const,
  WORKSPACE_PRICING_UPDATE_NOTIFICATION_VIEWED:
    'Pricing Update Notification Viewed' as const,
  WORKSPACE_PRICING_UPDATE_NOTIFICATION_LINK_CLICKED:
    'Pricing Update Notification Link Clicked' as const,
  WORKSPACE_PRICING_UPDATE_NOTIFICATION_GO_TO_SEATS_CLICKED:
    'Pricing Update Notification Go To Seats Clicked' as const,
  WORKSPACE_PRICING_UPDATE_NOTIFICATION_GO_TO_BILLING_CLICKED:
    'Pricing Update Notification Go to Billing Clicked' as const,
  WORKSPACE_PRICING_UPDATE_NOTIFICATION_GO_TO_PLANS_CLICKED:
    'Pricing Update Notification Go to Plans Clicked' as const,
  WORKSPACE_PRICING_UPDATE_NOTIFICATION_SEATS_CLICKED:
    'Pricing Update Notification Seats Clicked' as const,
  WORKSPACE_PRICING_UPDATE_NOTIFICATION_UPGRADE_NOW_CLICKED:
    'Pricing Update Notification Upgrade Now Clicked' as const,
  WORKSPACE_PRICING_UPDATE_NOTIFICATION_CLOSED:
    'Pricing Update Notification Closed' as const,
  WRITING_ASSISTANT_FREEFORM_FEEDBACK_SUBMITTED:
    'Writing Assistant Freeform Feedback Submitted' as const,
  OPTIMIZATION_NEW_OPTIMIZATION_STARTED: 'New Optimization Started' as const,
  OPTIMIZATION_VARIATION_EDITED: 'Variation Edited' as const,
  OPTIMIZATION_INSIGHTS_PANEL_CLICKED: 'Insights Panel Clicked' as const,
  OPTIMIZATION_GOALS_PANEL_CLICKED: 'Optimization Goal Panel Clicked' as const,
  OPTIMIZATION_OVERVIEW_PANEL_CLICKED:
    'Optimization Overview Panel Clicked' as const,
  OPTIMIZATION_OPTIMIZATION_SITE_PUBLISH_MODAL:
    'Optimization Site Publish Modal' as const,
  OPTIMIZATION_NEW_AUDIENCE_CREATED: 'New Audience Created' as const,
  OPTIMIZATION_LIMIT_CONDITION_CREATED: 'Limit Condition Created' as const,
  OPTIMIZATION_OPTIMIZE_FILTERS_APPLIED: 'Optimize Filters Applied' as const,
  OPTIMIZATION_OVERVIEW_FILTERS_APPLIED:
    'Optimization Overview Filters Applied' as const,
  OPTIMIZATION_NEW_OPTIMIZATION_CREATED: 'New Optimization Created' as const,
  OPTIMIZATION_NEW_OPTIMIZATION_CANCELED: 'New Optimization Canceled' as const,
  OPTIMIZATION_OPTIMIZATION_REPORT_OPENED:
    'Optimization Report Opened' as const,
  OPTIMIZATION_NEW_VARIATION_CLICKED: 'New Variation Clicked' as const,
  OPTIMIZATION_NEW_VARIATION_ADDED: 'New Variation Added' as const,
  OPTIMIZATION_OPTIMIZATION_SETTINGS_CLICKED:
    'Optimization Settings Clicked' as const,
  OPTIMIZATION_LAUNCH_OPTIMIZATION_VARIATIONS:
    'Launch Optimization Variations' as const,
  OPTIMIZATION_STOP_OPTIMIZATION: 'Stop Optimization' as const,
  OPTIMIZATION_DELETE_OPTIMIZATION_VARIATIONS:
    'Delete Optimization Variations' as const,
  OPTIMIZATION_ARCHIVE_OPTIMIZATION: 'Archive Optimization' as const,
  OPTIMIZATION_APPLY_OPTIMIZATION_VARIATION:
    'Apply Optimization Variation' as const,
  OPTIMIZATION_RENAME_OPTIMIZATION: 'Rename Optimization' as const,
  OPTIMIZATION_VARIATION_CONVERT_TO_REDIRECT_CLICKED:
    'Variation Convert to Redirect Clicked' as const,
  OPTIMIZATION_AI_SUGGESTION_EDIT_SUGGESTION_CLICKED:
    'AI Suggestion Edit Suggestion Clicked' as const,
  OPTIMIZATION_AI_SUGGESTION_INVALID_ELEMENT_SELECTED:
    'AI Suggestion Invalid Element Selected' as const,
  OPTIMIZATION_AI_SUGGESTION_GENERATION_FAILED:
    'AI Suggestion Generation Failed' as const,
  OPTIMIZATION_AI_SUGGESTION_NEXT_STEP_CLICKED:
    'AI Suggestion Next Step Clicked' as const,
  OPTIMIZATION_AI_SUGGESTION_TOGGLE_CLICKED:
    'AI Suggestion Toggle Clicked' as const,
  OPEN_GRAPH_IMAGE_SELECTOR_OPENED: 'Open Graph Image Selector Opened' as const,
  OPEN_GRAPH_IMAGE_SELECTOR_CLOSED: 'Open Graph Image Selector Closed' as const,
  COMPRESS_IMAGES_MODAL_OPENED: 'Compress Images Modal Opened' as const,
  COMPRESS_IMAGES_MODAL_CLOSED: 'Compress Images Modal Closed' as const,
  SITEMAP_INDEXING_TOGGLED: 'Sitemap Indexing Toggled' as const,
  SITEMAP_LEARN_MORE_LINK_CLICKED: 'Sitemap Learn More Link Clicked' as const,
  WORKSPACE_INVITE_BUTTON_CLICKED: 'Workspace Invitation CTA Clicked' as const,
  WORKSPACE_MEMBERS_CSV_EXPORTED: 'Workspace Members CSV Exported' as const,
  SITE_MEMBERS_CSV_EXPORTED: 'Site Members CSV Exported' as const,

  EVENT_EDITOR_LOGIN: 'Editor Login' as const,
  EVENT_EDITOR_PAGE_EDIT: 'Editor Page Edit' as const,
  EVENT_EDITOR_TEXT_EDIT: 'Editor Text Edit' as const,
  EVENT_EDITOR_IMAGE_EDIT: 'Editor Image Edit' as const,
  EVENT_EDITOR_IMAGE_UPLOAD: 'Editor Image Upload' as const,
  EVENT_EDITOR_ITEM_CREATE: 'Editor Item Create' as const,
  EVENT_EDITOR_ITEM_EDIT: 'Editor Item Edit' as const,
  EVENT_EDITOR_SITE_PUBLISH: 'Editor Site Publish' as const,

  EVENT_PREVIEW_ROLE_OPTION_SELECTED: 'Preview Role Option Selected' as const,

  DEFAULT_PREVIEW_ROLE_SET: 'Default Preview Role Set' as const,
  DEFAULT_PREVIEW_ROLE_CLEARED: 'Default Preview Role Cleared' as const,
} as const;
